import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useState, useCallback } from "react";
import {
  Plus, Zap, Power, Trash2, Clock, Mail, Share2, FileText, Search, Filter,
  Play, Copy, ChevronDown, ChevronRight, X, AlertTriangle, Check, Loader2,
  BarChart3, ArrowUpDown, MoreHorizontal, Eye, Edit3, Sparkles, Globe,
  Bell, Users, Target, Webhook, Timer, GitBranch, Tag, TrendingUp,
  CheckCircle2, XCircle, ArrowRight, Pause, RefreshCw, Activity,
} from "lucide-react";

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════
interface ActionStep {
  id: string;
  type: string;
  label: string;
  config: Record<string, any>;
}

interface AutomationRun {
  id: string;
  status: string;
  duration?: number;
  stepsRun: number;
  stepsTotal: number;
  output?: any[];
  error?: string;
  triggeredBy?: string;
  createdAt: string;
}

interface Automation {
  id: string;
  name: string;
  description?: string;
  trigger: string;
  triggerConfig?: any;
  actions: ActionStep[];
  status: string;
  isActive: boolean;
  priority: string;
  lastRunAt?: string;
  nextRunAt?: string;
  runCount: number;
  successCount: number;
  failureCount: number;
  createdAt: string;
  updatedAt: string;
  runs?: AutomationRun[];
}

interface Stats {
  total: number; active: number; paused: number; draft: number;
  totalRuns: number; totalSuccesses: number; totalFailures: number;
  successRate: number; recentRuns: number;
}

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════
const TRIGGERS = [
  { id: "schedule", label: "Scheduled", icon: Clock, color: "blue", description: "Run on a schedule (daily, weekly, custom cron)" },
  { id: "new_subscriber", label: "New Subscriber", icon: Users, color: "green", description: "Triggered when someone subscribes" },
  { id: "content_published", label: "Content Published", icon: FileText, color: "purple", description: "When content is published" },
  { id: "campaign_started", label: "Campaign Started", icon: Target, color: "orange", description: "When a campaign launches" },
  { id: "lead_captured", label: "Lead Captured", icon: Sparkles, color: "yellow", description: "When a new lead is captured" },
  { id: "form_submit", label: "Form Submitted", icon: FileText, color: "pink", description: "When a web form is filled out" },
  { id: "webhook", label: "Webhook", icon: Webhook, color: "gray", description: "Triggered via external webhook" },
];

const ACTION_TYPES = [
  { id: "send_email", label: "Send Email", icon: Mail, color: "blue", category: "communication" },
  { id: "post_social", label: "Post to Social", icon: Share2, color: "purple", category: "communication" },
  { id: "notify_team", label: "Notify Team", icon: Bell, color: "yellow", category: "communication" },
  { id: "delay", label: "Wait / Delay", icon: Timer, color: "gray", category: "flow" },
  { id: "condition", label: "If / Condition", icon: GitBranch, color: "orange", category: "flow" },
  { id: "webhook", label: "Call Webhook", icon: Globe, color: "indigo", category: "integration" },
  { id: "update_contact", label: "Tag Contact", icon: Tag, color: "green", category: "crm" },
  { id: "add_to_campaign", label: "Add to Campaign", icon: Target, color: "pink", category: "crm" },
  { id: "score_lead", label: "Score Lead", icon: TrendingUp, color: "cyan", category: "crm" },
  { id: "segment", label: "Move to Segment", icon: Users, color: "violet", category: "crm" },
];

const PRIORITIES = [
  { id: "low", label: "Low", color: "text-gray-500 bg-gray-100" },
  { id: "medium", label: "Medium", color: "text-blue-700 bg-blue-50" },
  { id: "high", label: "High", color: "text-orange-700 bg-orange-50" },
  { id: "critical", label: "Critical", color: "text-red-700 bg-red-50" },
];

const STATUS_MAP: Record<string, { label: string; color: string; icon: any }> = {
  active: { label: "Active", color: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle2 },
  paused: { label: "Paused", color: "bg-yellow-50 text-yellow-700 border-yellow-200", icon: Pause },
  draft: { label: "Draft", color: "bg-gray-50 text-gray-500 border-gray-200", icon: Edit3 },
  error: { label: "Error", color: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
};

const TEMPLATES = [
  { name: "Welcome Email Sequence", trigger: "new_subscriber", priority: "high",
    description: "Automatically send a welcome drip sequence to new subscribers with 3 emails over 7 days.",
    actions: [
      { id: "1", type: "send_email", label: "Send Welcome Email", config: { subject: "Welcome to MigraTeck!", to: "subscriber" } },
      { id: "2", type: "delay", label: "Wait 2 Days", config: { minutes: 2880 } },
      { id: "3", type: "send_email", label: "Send Getting Started Guide", config: { subject: "Getting Started Guide", to: "subscriber" } },
      { id: "4", type: "delay", label: "Wait 5 Days", config: { minutes: 7200 } },
      { id: "5", type: "send_email", label: "Send Tips & Tricks", config: { subject: "Tips & Tricks", to: "subscriber" } },
      { id: "6", type: "update_contact", label: "Tag as Onboarded", config: { tag: "onboarded" } },
    ],
  },
  { name: "Auto-Post to Social Media", trigger: "content_published", priority: "medium",
    description: "Publish newly created content across all connected social platforms automatically.",
    actions: [
      { id: "1", type: "post_social", label: "Post to Facebook", config: { platforms: ["facebook"] } },
      { id: "2", type: "post_social", label: "Post to Instagram", config: { platforms: ["instagram"] } },
      { id: "3", type: "post_social", label: "Post to X (Twitter)", config: { platforms: ["twitter"] } },
      { id: "4", type: "post_social", label: "Post to LinkedIn", config: { platforms: ["linkedin"] } },
      { id: "5", type: "notify_team", label: "Notify Marketing Team", config: { channel: "marketing" } },
    ],
  },
  { name: "Lead Follow-Up", trigger: "lead_captured", priority: "critical",
    description: "Instantly engage new leads with a follow-up email, score them, and notify sales.",
    actions: [
      { id: "1", type: "score_lead", label: "Score +20 Points", config: { points: 20 } },
      { id: "2", type: "send_email", label: "Send Thank You Email", config: { subject: "Thanks for your interest!", to: "lead" } },
      { id: "3", type: "update_contact", label: "Tag as New Lead", config: { tag: "new-lead" } },
      { id: "4", type: "notify_team", label: "Alert Sales Team", config: { channel: "sales" } },
      { id: "5", type: "add_to_campaign", label: "Add to Nurture Campaign", config: { campaignName: "Lead Nurture" } },
    ],
  },
  { name: "Weekly Newsletter", trigger: "schedule", priority: "medium",
    description: "Compile top content and send a curated newsletter every Monday.",
    actions: [
      { id: "1", type: "condition", label: "Check if content available", config: { field: "contentCount", operator: "gt", value: "0" } },
      { id: "2", type: "send_email", label: "Send Newsletter", config: { subject: "This Week at MigraTeck", to: "all-subscribers" } },
      { id: "3", type: "notify_team", label: "Notify Marketing", config: { channel: "marketing" } },
    ],
  },
  { name: "Campaign Performance Report", trigger: "schedule", priority: "low",
    description: "Generate and distribute weekly campaign performance reports every Friday.",
    actions: [
      { id: "1", type: "webhook", label: "Generate Report", config: { url: "https://api.example.com/reports/generate" } },
      { id: "2", type: "delay", label: "Wait for generation", config: { minutes: 5 } },
      { id: "3", type: "send_email", label: "Email Report to Team", config: { subject: "Weekly Campaign Report", to: "team" } },
    ],
  },
  { name: "Social Engagement Bot", trigger: "schedule", priority: "low",
    description: "Automatically engage with brand mentions and respond to comments.",
    actions: [
      { id: "1", type: "webhook", label: "Fetch Mentions", config: { url: "https://api.example.com/mentions" } },
      { id: "2", type: "condition", label: "Check sentiment", config: { field: "sentiment", operator: "equals", value: "positive" } },
      { id: "3", type: "post_social", label: "Auto-Like Posts", config: { platforms: ["all"] } },
      { id: "4", type: "notify_team", label: "Notify if Negative", config: { channel: "support" } },
    ],
  },
  { name: "Abandoned Cart Recovery", trigger: "webhook", priority: "critical",
    description: "Send recovery emails when a user abandons their cart.",
    actions: [
      { id: "1", type: "delay", label: "Wait 1 Hour", config: { minutes: 60 } },
      { id: "2", type: "send_email", label: "Reminder Email", config: { subject: "You left something behind!", to: "customer" } },
      { id: "3", type: "delay", label: "Wait 24 Hours", config: { minutes: 1440 } },
      { id: "4", type: "condition", label: "Check if purchased", config: { field: "purchased", operator: "equals", value: "false" } },
      { id: "5", type: "send_email", label: "Discount Offer", config: { subject: "Here's 10% off!", to: "customer" } },
      { id: "6", type: "score_lead", label: "Adjust Score", config: { points: -5 } },
    ],
  },
  { name: "Re-Engagement Campaign", trigger: "schedule", priority: "high",
    description: "Target inactive contacts with a re-engagement series.",
    actions: [
      { id: "1", type: "segment", label: "Find Inactive Contacts", config: { segment: "inactive-30-days" } },
      { id: "2", type: "send_email", label: "We Miss You Email", config: { subject: "We miss you!", to: "segment" } },
      { id: "3", type: "delay", label: "Wait 3 Days", config: { minutes: 4320 } },
      { id: "4", type: "condition", label: "Check if opened", config: { field: "emailOpened", operator: "equals", value: "true" } },
      { id: "5", type: "update_contact", label: "Tag as Re-Engaged", config: { tag: "re-engaged" } },
    ],
  },
];

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export default function Automations() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<"list" | "detail" | "create" | "edit">("list");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterTrigger, setFilterTrigger] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  // Queries
  const { data: stats } = useQuery({
    queryKey: ["automation-stats"],
    queryFn: () => api.get<Stats>("/automations/stats"),
  });

  const { data: automations = [], isLoading } = useQuery({
    queryKey: ["automations", searchQuery, filterStatus, filterTrigger],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchQuery) params.set("search", searchQuery);
      if (filterStatus) params.set("status", filterStatus);
      if (filterTrigger) params.set("trigger", filterTrigger);
      return api.get<Automation[]>(`/automations?${params}`);
    },
  });

  const { data: detail } = useQuery({
    queryKey: ["automation", selectedId],
    queryFn: () => api.get<Automation & { runs: AutomationRun[] }>(`/automations/${selectedId}`),
    enabled: !!selectedId && (view === "detail" || view === "edit"),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (body: any) => api.post<Automation>("/automations", body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["automations"] }); queryClient.invalidateQueries({ queryKey: ["automation-stats"] }); setView("list"); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => api.put<Automation>(`/automations/${id}`, body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["automations"] }); queryClient.invalidateQueries({ queryKey: ["automation-stats"] }); queryClient.invalidateQueries({ queryKey: ["automation", selectedId] }); setView("detail"); },
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => api.patch<Automation>(`/automations/${id}/toggle`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["automations"] }); queryClient.invalidateQueries({ queryKey: ["automation-stats"] }); queryClient.invalidateQueries({ queryKey: ["automation", selectedId] }); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/automations/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["automations"] }); queryClient.invalidateQueries({ queryKey: ["automation-stats"] }); setView("list"); setSelectedId(null); },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => api.post<Automation>(`/automations/${id}/duplicate`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["automations"] }); queryClient.invalidateQueries({ queryKey: ["automation-stats"] }); },
  });

  const runMutation = useMutation({
    mutationFn: (id: string) => api.post<AutomationRun>(`/automations/${id}/run`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["automations"] }); queryClient.invalidateQueries({ queryKey: ["automation-stats"] }); queryClient.invalidateQueries({ queryKey: ["automation", selectedId] }); },
  });

  const bulkMutation = useMutation({
    mutationFn: (body: { action: string; ids: string[] }) => api.post("/automations/bulk", body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["automations"] }); queryClient.invalidateQueries({ queryKey: ["automation-stats"] }); setSelectedIds(new Set()); },
  });

  const openDetail = (id: string) => { setSelectedId(id); setView("detail"); };
  const openEdit = (id: string) => { setSelectedId(id); setView("edit"); };

  if (view === "create") return <AutomationBuilder onSave={(data) => createMutation.mutate(data)} onCancel={() => setView("list")} saving={createMutation.isPending} />;
  if (view === "edit" && detail) return <AutomationBuilder automation={detail} onSave={(data) => updateMutation.mutate({ id: detail.id, body: data })} onCancel={() => setView("detail")} saving={updateMutation.isPending} />;
  if (view === "detail" && detail) return <AutomationDetail automation={detail} onBack={() => { setView("list"); setSelectedId(null); }} onEdit={() => openEdit(detail.id)} onToggle={() => toggleMutation.mutate(detail.id)} onDelete={() => { if (confirm("Delete this automation permanently?")) deleteMutation.mutate(detail.id); }} onDuplicate={() => duplicateMutation.mutate(detail.id)} onRun={() => runMutation.mutate(detail.id)} running={runMutation.isPending} />;

  // ─── List View ──────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Automations</h1>
          <p className="text-sm text-gray-500 mt-1">Build and manage automated marketing workflows</p>
        </div>
        <button onClick={() => setView("create")} className="btn-primary">
          <Plus className="w-4 h-4" /> New Automation
        </button>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: "Total", value: stats.total, icon: Zap, color: "text-indigo-600 bg-indigo-50" },
            { label: "Active", value: stats.active, icon: CheckCircle2, color: "text-green-600 bg-green-50" },
            { label: "Paused", value: stats.paused, icon: Pause, color: "text-yellow-600 bg-yellow-50" },
            { label: "Total Runs", value: stats.totalRuns, icon: Activity, color: "text-blue-600 bg-blue-50" },
            { label: "Success Rate", value: `${stats.successRate}%`, icon: TrendingUp, color: "text-emerald-600 bg-emerald-50" },
          ].map((s) => (
            <div key={s.label} className="card p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search + Filters + Bulk */}
      <div className="card p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="input pl-10 w-full"
              placeholder="Search automations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button onClick={() => setShowFilters(!showFilters)} className={`btn-secondary text-sm ${showFilters ? "bg-indigo-50 text-indigo-600 border-indigo-200" : ""}`}>
            <Filter className="w-4 h-4" /> Filters
            {(filterStatus || filterTrigger) && <span className="w-2 h-2 bg-indigo-600 rounded-full" />}
          </button>

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-500">{selectedIds.size} selected</span>
              <button onClick={() => bulkMutation.mutate({ action: "activate", ids: [...selectedIds] })} className="text-xs bg-green-50 text-green-700 px-3 py-1.5 rounded-lg hover:bg-green-100 font-medium">Activate</button>
              <button onClick={() => bulkMutation.mutate({ action: "pause", ids: [...selectedIds] })} className="text-xs bg-yellow-50 text-yellow-700 px-3 py-1.5 rounded-lg hover:bg-yellow-100 font-medium">Pause</button>
              <button onClick={() => { if (confirm(`Delete ${selectedIds.size} automations?`)) bulkMutation.mutate({ action: "delete", ids: [...selectedIds] }); }} className="text-xs bg-red-50 text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-100 font-medium">Delete</button>
            </div>
          )}
        </div>

        {showFilters && (
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100 flex-wrap">
            <select className="input text-sm w-auto" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="draft">Draft</option>
              <option value="error">Error</option>
            </select>
            <select className="input text-sm w-auto" value={filterTrigger} onChange={(e) => setFilterTrigger(e.target.value)}>
              <option value="">All Triggers</option>
              {TRIGGERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            {(filterStatus || filterTrigger) && (
              <button onClick={() => { setFilterStatus(""); setFilterTrigger(""); }} className="text-xs text-gray-500 hover:text-gray-700">
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Automations List */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : automations.length > 0 ? (
        <div className="space-y-3">
          {automations.map((auto) => {
            const trigger = TRIGGERS.find((t) => t.id === auto.trigger);
            const TriggerIcon = trigger?.icon || Zap;
            const statusMeta = STATUS_MAP[auto.status] || STATUS_MAP.draft;
            const StatusIcon = statusMeta.icon;
            const successRate = auto.runCount > 0 ? Math.round((auto.successCount / auto.runCount) * 100) : 0;
            const priorityMeta = PRIORITIES.find((p) => p.id === auto.priority) || PRIORITIES[1];
            const isSelected = selectedIds.has(auto.id);

            return (
              <div key={auto.id} className={`card p-0 overflow-hidden transition-all hover:shadow-md ${isSelected ? "ring-2 ring-indigo-500" : ""} ${auto.isActive ? "border-l-4 border-l-green-500" : auto.status === "error" ? "border-l-4 border-l-red-500" : ""}`}>
                <div className="p-5 flex items-start gap-4">
                  {/* Checkbox */}
                  <label className="mt-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {
                        const next = new Set(selectedIds);
                        isSelected ? next.delete(auto.id) : next.add(auto.id);
                        setSelectedIds(next);
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </label>

                  {/* Icon */}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-${trigger?.color || "gray"}-100`}>
                    <TriggerIcon className={`w-5 h-5 text-${trigger?.color || "gray"}-600`} />
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDetail(auto.id)}>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">{auto.name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${statusMeta.color}`}>
                        {statusMeta.label}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${priorityMeta.color}`}>
                        {priorityMeta.label}
                      </span>
                    </div>
                    {auto.description && (
                      <p className="text-sm text-gray-500 mt-0.5 truncate">{auto.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><TriggerIcon className="w-3 h-3" /> {trigger?.label || auto.trigger}</span>
                      <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {auto.actions?.length || 0} steps</span>
                      <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> {auto.runCount} runs</span>
                      {auto.runCount > 0 && (
                        <span className={`flex items-center gap-1 ${successRate >= 80 ? "text-green-500" : successRate >= 50 ? "text-yellow-500" : "text-red-500"}`}>
                          <TrendingUp className="w-3 h-3" /> {successRate}% success
                        </span>
                      )}
                      {auto.lastRunAt && (
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Last: {timeAgo(auto.lastRunAt)}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); runMutation.mutate(auto.id); }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Run Now">
                      <Play className="w-4 h-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); toggleMutation.mutate(auto.id); }} className={`p-2 rounded-lg transition-colors ${auto.isActive ? "text-green-600 hover:bg-green-50" : "text-gray-400 hover:bg-gray-100"}`} title={auto.isActive ? "Pause" : "Activate"}>
                      <Power className="w-4 h-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); duplicateMutation.mutate(auto.id); }} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Duplicate">
                      <Copy className="w-4 h-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); if (confirm("Delete automation?")) deleteMutation.mutate(auto.id); }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Mini run history */}
                {auto.runs && auto.runs.length > 0 && (
                  <div className="px-5 pb-3 pt-0 flex items-center gap-1.5">
                    <span className="text-xs text-gray-400 mr-1">Recent:</span>
                    {auto.runs.map((run) => (
                      <div
                        key={run.id}
                        className={`w-6 h-2 rounded-full ${run.status === "success" ? "bg-green-400" : run.status === "failure" ? "bg-red-400" : "bg-gray-300"}`}
                        title={`${run.status} — ${new Date(run.createdAt).toLocaleString()}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card p-16 text-center">
          <div className="w-16 h-16 bg-yellow-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Zap className="w-8 h-8 text-yellow-500" />
          </div>
          <h3 className="text-lg font-semibold mb-1">No automations yet</h3>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            Automate repetitive marketing tasks like email sequences, social posting, lead scoring, and more.
          </p>
          <button onClick={() => setView("create")} className="btn-primary mx-auto">
            <Plus className="w-4 h-4" /> Create Your First Automation
          </button>
        </div>
      )}

      {/* Templates */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">Automation Templates</h2>
            <p className="text-xs text-gray-400 mt-0.5">{TEMPLATES.length} pre-built workflows ready to use</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TEMPLATES.map((tpl) => {
            const trigger = TRIGGERS.find((t) => t.id === tpl.trigger);
            const TriggerIcon = trigger?.icon || Zap;
            const priorityMeta = PRIORITIES.find((p) => p.id === tpl.priority) || PRIORITIES[1];
            return (
              <button
                key={tpl.name}
                onClick={() => {
                  setSelectedId(null);
                  setView("create");
                  // Use a slight delay so the builder mounts first
                  setTimeout(() => {
                    const ev = new CustomEvent("template-load", { detail: tpl });
                    window.dispatchEvent(ev);
                  }, 100);
                }}
                className="card p-4 text-left hover:border-indigo-200 hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${trigger?.color || "gray"}-100`}>
                    <TriggerIcon className={`w-4 h-4 text-${trigger?.color || "gray"}-600`} />
                  </div>
                  <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${priorityMeta.color}`}>{priorityMeta.label}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">{tpl.name}</span>
                <p className="text-xs text-gray-400 mt-1 line-clamp-2">{tpl.description}</p>
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100 text-xs text-gray-400">
                  <span>{tpl.actions.length} steps</span>
                  <span>•</span>
                  <span>{trigger?.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// UTILITY
// ═══════════════════════════════════════════════════════
function timeAgo(dateStr: string) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function uid() { return Math.random().toString(36).slice(2, 10); }

// ═══════════════════════════════════════════════════════
// AUTOMATION BUILDER (Create / Edit)
// ═══════════════════════════════════════════════════════
function AutomationBuilder({
  automation,
  onSave,
  onCancel,
  saving,
}: {
  automation?: Automation;
  onSave: (data: any) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [name, setName] = useState(automation?.name || "");
  const [description, setDescription] = useState(automation?.description || "");
  const [trigger, setTrigger] = useState(automation?.trigger || "schedule");
  const [triggerConfig, setTriggerConfig] = useState<any>(automation?.triggerConfig || {});
  const [actions, setActions] = useState<ActionStep[]>(automation?.actions || []);
  const [priority, setPriority] = useState(automation?.priority || "medium");
  const [status, setStatus] = useState(automation?.status || "draft");
  const [showAddAction, setShowAddAction] = useState(false);
  const [editingAction, setEditingAction] = useState<string | null>(null);

  // Listen for template events
  useState(() => {
    const handler = (e: any) => {
      const tpl = e.detail;
      if (tpl) {
        setName(tpl.name);
        setDescription(tpl.description || "");
        setTrigger(tpl.trigger);
        setPriority(tpl.priority || "medium");
        setActions(tpl.actions || []);
      }
    };
    window.addEventListener("template-load", handler);
    return () => window.removeEventListener("template-load", handler);
  });

  const addAction = (type: string) => {
    const meta = ACTION_TYPES.find((a) => a.id === type);
    setActions([...actions, { id: uid(), type, label: meta?.label || type, config: {} }]);
    setShowAddAction(false);
  };

  const removeAction = (id: string) => setActions(actions.filter((a) => a.id !== id));
  const moveAction = (idx: number, dir: -1 | 1) => {
    const next = [...actions];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setActions(next);
  };

  const updateAction = (id: string, updates: Partial<ActionStep>) => {
    setActions(actions.map((a) => a.id === id ? { ...a, ...updates } : a));
  };

  const updateActionConfig = (id: string, key: string, value: any) => {
    setActions(actions.map((a) => a.id === id ? { ...a, config: { ...a.config, [key]: value } } : a));
  };

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name, description, trigger, triggerConfig, actions, priority, status });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
            <ChevronRight className="w-5 h-5 rotate-180" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {automation ? "Edit Automation" : "Create Automation"}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {automation ? "Modify your workflow configuration" : "Build a new automated workflow"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="btn-secondary">Cancel</button>
          <button onClick={() => { setStatus("draft"); handleSave(); }} disabled={saving || !name.trim()} className="btn-secondary flex items-center gap-2">
            <Edit3 className="w-4 h-4" /> Save as Draft
          </button>
          <button onClick={() => { setStatus("active"); handleSave(); }} disabled={saving || !name.trim() || actions.length === 0} className="btn-primary">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
            {automation ? "Update & Activate" : "Create & Activate"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Config */}
        <div className="lg:col-span-1 space-y-6">
          {/* Basic Info */}
          <div className="card p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Configuration</h2>
            <div>
              <label className="label">Name *</label>
              <input className="input" placeholder="e.g. Welcome Email Sequence" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="label">Description</label>
              <textarea className="input min-h-[80px]" placeholder="Describe what this automation does..." value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div>
              <label className="label">Priority</label>
              <div className="grid grid-cols-4 gap-2">
                {PRIORITIES.map((p) => (
                  <button key={p.id} onClick={() => setPriority(p.id)} className={`px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${priority === p.id ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 hover:border-gray-300"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Trigger */}
          <div className="card p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Trigger</h2>
            <p className="text-xs text-gray-400">What starts this automation?</p>
            <div className="space-y-2">
              {TRIGGERS.map((t) => (
                <label key={t.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${trigger === t.id ? "border-indigo-500 bg-indigo-50 shadow-sm" : "border-gray-200 hover:border-gray-300"}`}>
                  <input type="radio" name="trigger" value={t.id} checked={trigger === t.id} onChange={() => { setTrigger(t.id); setTriggerConfig({}); }} className="hidden" />
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${t.color}-100`}>
                    <t.icon className={`w-4 h-4 text-${t.color}-600`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{t.label}</p>
                    <p className="text-xs text-gray-400">{t.description}</p>
                  </div>
                  {trigger === t.id && <Check className="w-4 h-4 text-indigo-600" />}
                </label>
              ))}
            </div>

            {/* Trigger Config */}
            {trigger === "schedule" && (
              <div className="mt-3 space-y-3 bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-xs font-medium text-blue-700">Schedule Configuration</p>
                <div>
                  <label className="text-xs text-blue-600">Frequency</label>
                  <select className="input text-sm mt-1" value={triggerConfig.frequency || "daily"} onChange={(e) => setTriggerConfig({ ...triggerConfig, frequency: e.target.value })}>
                    <option value="hourly">Every Hour</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="custom">Custom Cron</option>
                  </select>
                </div>
                {triggerConfig.frequency === "custom" && (
                  <div>
                    <label className="text-xs text-blue-600">Cron Expression</label>
                    <input className="input text-sm mt-1 font-mono" placeholder="0 9 * * MON" value={triggerConfig.cron || ""} onChange={(e) => setTriggerConfig({ ...triggerConfig, cron: e.target.value })} />
                  </div>
                )}
                <div>
                  <label className="text-xs text-blue-600">Time</label>
                  <input type="time" className="input text-sm mt-1" value={triggerConfig.time || "09:00"} onChange={(e) => setTriggerConfig({ ...triggerConfig, time: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-blue-600">Timezone</label>
                  <select className="input text-sm mt-1" value={triggerConfig.timezone || "America/New_York"} onChange={(e) => setTriggerConfig({ ...triggerConfig, timezone: e.target.value })}>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
              </div>
            )}

            {trigger === "webhook" && (
              <div className="mt-3 space-y-2 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-700">Webhook URL</p>
                <div className="flex items-center gap-2 bg-white rounded-md border border-gray-200 p-2">
                  <code className="text-xs font-mono text-gray-600 flex-1 truncate">
                    https://marketing.migrahosting.com/api/webhooks/auto/{uid()}
                  </code>
                  <button className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">Copy</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Actions / Workflow Builder */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Workflow Steps</h2>
                <p className="text-xs text-gray-400 mt-0.5">{actions.length} action{actions.length !== 1 ? "s" : ""} configured</p>
              </div>
            </div>

            {/* Actions List */}
            {actions.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                <Zap className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-3">No steps added yet</p>
                <button onClick={() => setShowAddAction(true)} className="btn-primary text-sm">
                  <Plus className="w-4 h-4" /> Add First Step
                </button>
              </div>
            ) : (
              <div className="space-y-0">
                {actions.map((action, idx) => {
                  const meta = ACTION_TYPES.find((a) => a.id === action.type);
                  const ActionIcon = meta?.icon || Zap;
                  const isEditing = editingAction === action.id;

                  return (
                    <div key={action.id}>
                      {/* Connector Line */}
                      {idx > 0 && (
                        <div className="flex justify-center py-1">
                          <div className="w-0.5 h-6 bg-gray-200" />
                        </div>
                      )}

                      <div className={`border rounded-xl p-4 transition-all ${isEditing ? "border-indigo-300 bg-indigo-50/30 shadow-sm" : "border-gray-200 hover:border-gray-300"}`}>
                        <div className="flex items-center gap-3">
                          {/* Step number */}
                          <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {idx + 1}
                          </div>

                          {/* Icon */}
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${meta?.color || "gray"}-100 flex-shrink-0`}>
                            <ActionIcon className={`w-4 h-4 text-${meta?.color || "gray"}-600`} />
                          </div>

                          {/* Label */}
                          <div className="flex-1 min-w-0">
                            {isEditing ? (
                              <input className="input text-sm" value={action.label} onChange={(e) => updateAction(action.id, { label: e.target.value })} autoFocus />
                            ) : (
                              <>
                                <p className="text-sm font-medium text-gray-900">{action.label}</p>
                                <p className="text-xs text-gray-400">{meta?.label}</p>
                              </>
                            )}
                          </div>

                          {/* Controls */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={() => moveAction(idx, -1)} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5 rotate-180" /></button>
                            <button onClick={() => moveAction(idx, 1)} disabled={idx === actions.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setEditingAction(isEditing ? null : action.id)} className={`p-1 rounded ${isEditing ? "text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}><Edit3 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => removeAction(action.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>

                        {/* Expanded Config */}
                        {isEditing && (
                          <div className="mt-3 pt-3 border-t border-indigo-100 space-y-3">
                            <ActionConfig action={action} onUpdate={(key, val) => updateActionConfig(action.id, key, val)} />
                            <button onClick={() => setEditingAction(null)} className="text-xs text-indigo-600 font-medium hover:text-indigo-700">
                              Done editing ↑
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Add step connector */}
                <div className="flex justify-center py-1">
                  <div className="w-0.5 h-6 bg-gray-200" />
                </div>
                <div className="flex justify-center">
                  <button onClick={() => setShowAddAction(true)} className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors">
                    <Plus className="w-4 h-4" /> Add Step
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Action Modal */}
      {showAddAction && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddAction(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 text-white flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Add Action Step</h3>
                <p className="text-sm text-white/70">Choose what this step should do</p>
              </div>
              <button onClick={() => setShowAddAction(false)} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-4 max-h-[50vh] overflow-y-auto">
              {["communication", "flow", "crm", "integration"].map((cat) => {
                const items = ACTION_TYPES.filter((a) => a.category === cat);
                return (
                  <div key={cat} className="mb-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
                      {cat === "communication" ? "📧 Communication" : cat === "flow" ? "🔄 Flow Control" : cat === "crm" ? "👥 CRM" : "🔌 Integrations"}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {items.map((a) => (
                        <button key={a.id} onClick={() => addAction(a.id)} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${a.color}-100`}>
                            <a.icon className={`w-4 h-4 text-${a.color}-600`} />
                          </div>
                          <span className="text-sm font-medium text-gray-900">{a.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// ACTION CONFIG FORM
// ═══════════════════════════════════════════════════════
function ActionConfig({ action, onUpdate }: { action: ActionStep; onUpdate: (key: string, value: any) => void }) {
  switch (action.type) {
    case "send_email":
      return (
        <div className="space-y-2">
          <div><label className="text-xs text-gray-500">To</label><input className="input text-sm" placeholder="subscriber, lead, team, or email" value={action.config.to || ""} onChange={(e) => onUpdate("to", e.target.value)} /></div>
          <div><label className="text-xs text-gray-500">Subject</label><input className="input text-sm" placeholder="Email subject line" value={action.config.subject || ""} onChange={(e) => onUpdate("subject", e.target.value)} /></div>
          <div><label className="text-xs text-gray-500">Template</label>
            <select className="input text-sm" value={action.config.template || ""} onChange={(e) => onUpdate("template", e.target.value)}>
              <option value="">Default Template</option><option value="welcome">Welcome</option><option value="notification">Notification</option><option value="promotional">Promotional</option><option value="digest">Digest</option>
            </select>
          </div>
        </div>
      );
    case "post_social":
      return (
        <div className="space-y-2">
          <div><label className="text-xs text-gray-500">Platforms</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {["facebook", "instagram", "twitter", "linkedin", "tiktok"].map((p) => {
                const selected = (action.config.platforms || []).includes(p);
                return (
                  <button key={p} onClick={() => {
                    const current = action.config.platforms || [];
                    onUpdate("platforms", selected ? current.filter((x: string) => x !== p) : [...current, p]);
                  }} className={`px-3 py-1.5 text-xs rounded-lg border font-medium ${selected ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>
          <div><label className="text-xs text-gray-500">Caption (optional)</label><textarea className="input text-sm min-h-[60px]" placeholder="Post caption..." value={action.config.caption || ""} onChange={(e) => onUpdate("caption", e.target.value)} /></div>
        </div>
      );
    case "delay":
      return (
        <div className="space-y-2">
          <div><label className="text-xs text-gray-500">Wait Duration</label>
            <div className="flex items-center gap-2 mt-1">
              <input type="number" className="input text-sm w-20" min={1} value={action.config.amount || 5} onChange={(e) => {
                const amount = parseInt(e.target.value) || 1;
                const unit = action.config.unit || "minutes";
                const multiplier = unit === "hours" ? 60 : unit === "days" ? 1440 : 1;
                onUpdate("amount", amount);
                onUpdate("minutes", amount * multiplier);
              }} />
              <select className="input text-sm w-auto" value={action.config.unit || "minutes"} onChange={(e) => {
                const unit = e.target.value;
                const amount = action.config.amount || 5;
                const multiplier = unit === "hours" ? 60 : unit === "days" ? 1440 : 1;
                onUpdate("unit", unit);
                onUpdate("minutes", amount * multiplier);
              }}>
                <option value="minutes">Minutes</option><option value="hours">Hours</option><option value="days">Days</option>
              </select>
            </div>
          </div>
        </div>
      );
    case "condition":
      return (
        <div className="space-y-2">
          <div><label className="text-xs text-gray-500">Field</label><input className="input text-sm" placeholder="e.g. email_opened, lead_score" value={action.config.field || ""} onChange={(e) => onUpdate("field", e.target.value)} /></div>
          <div className="flex gap-2">
            <div className="flex-1"><label className="text-xs text-gray-500">Operator</label>
              <select className="input text-sm" value={action.config.operator || "equals"} onChange={(e) => onUpdate("operator", e.target.value)}>
                <option value="equals">Equals</option><option value="not_equals">Not Equals</option><option value="gt">Greater Than</option><option value="lt">Less Than</option><option value="contains">Contains</option>
              </select>
            </div>
            <div className="flex-1"><label className="text-xs text-gray-500">Value</label><input className="input text-sm" placeholder="Value" value={action.config.value || ""} onChange={(e) => onUpdate("value", e.target.value)} /></div>
          </div>
        </div>
      );
    case "webhook":
      return (
        <div className="space-y-2">
          <div><label className="text-xs text-gray-500">URL</label><input className="input text-sm font-mono" placeholder="https://api.example.com/hook" value={action.config.url || ""} onChange={(e) => onUpdate("url", e.target.value)} /></div>
          <div><label className="text-xs text-gray-500">Method</label>
            <select className="input text-sm" value={action.config.method || "POST"} onChange={(e) => onUpdate("method", e.target.value)}>
              <option value="POST">POST</option><option value="GET">GET</option><option value="PUT">PUT</option>
            </select>
          </div>
        </div>
      );
    case "update_contact":
      return (
        <div><label className="text-xs text-gray-500">Tag / Label</label><input className="input text-sm" placeholder="e.g. vip, onboarded, churning" value={action.config.tag || ""} onChange={(e) => onUpdate("tag", e.target.value)} /></div>
      );
    case "notify_team":
      return (
        <div className="space-y-2">
          <div><label className="text-xs text-gray-500">Channel</label><input className="input text-sm" placeholder="e.g. marketing, sales, support" value={action.config.channel || ""} onChange={(e) => onUpdate("channel", e.target.value)} /></div>
          <div><label className="text-xs text-gray-500">Message (optional)</label><input className="input text-sm" placeholder="Custom notification message" value={action.config.message || ""} onChange={(e) => onUpdate("message", e.target.value)} /></div>
        </div>
      );
    case "add_to_campaign":
      return (
        <div><label className="text-xs text-gray-500">Campaign Name</label><input className="input text-sm" placeholder="e.g. Lead Nurture, Welcome Series" value={action.config.campaignName || ""} onChange={(e) => onUpdate("campaignName", e.target.value)} /></div>
      );
    case "score_lead":
      return (
        <div><label className="text-xs text-gray-500">Points to Add</label><input type="number" className="input text-sm w-32" value={action.config.points || 10} onChange={(e) => onUpdate("points", parseInt(e.target.value) || 0)} /></div>
      );
    case "segment":
      return (
        <div><label className="text-xs text-gray-500">Segment Name</label><input className="input text-sm" placeholder="e.g. Engaged, VIP, Churning" value={action.config.segment || ""} onChange={(e) => onUpdate("segment", e.target.value)} /></div>
      );
    default:
      return <p className="text-xs text-gray-400">No configuration needed for this action.</p>;
  }
}

// ═══════════════════════════════════════════════════════
// AUTOMATION DETAIL VIEW
// ═══════════════════════════════════════════════════════
function AutomationDetail({
  automation,
  onBack,
  onEdit,
  onToggle,
  onDelete,
  onDuplicate,
  onRun,
  running,
}: {
  automation: Automation & { runs?: AutomationRun[] };
  onBack: () => void;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onRun: () => void;
  running: boolean;
}) {
  const [showRuns, setShowRuns] = useState(false);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const trigger = TRIGGERS.find((t) => t.id === automation.trigger);
  const TriggerIcon = trigger?.icon || Zap;
  const statusMeta = STATUS_MAP[automation.status] || STATUS_MAP.draft;
  const StatusIcon = statusMeta.icon;
  const priorityMeta = PRIORITIES.find((p) => p.id === automation.priority) || PRIORITIES[1];
  const successRate = automation.runCount > 0 ? Math.round((automation.successCount / automation.runCount) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
            <ChevronRight className="w-5 h-5 rotate-180" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{automation.name}</h1>
              <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${statusMeta.color}`}>{statusMeta.label}</span>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${priorityMeta.color}`}>{priorityMeta.label}</span>
            </div>
            {automation.description && <p className="text-sm text-gray-500 mt-1">{automation.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onRun} disabled={running} className="btn-secondary text-sm flex items-center gap-2">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Test Run
          </button>
          <button onClick={onToggle} className={`text-sm px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${automation.isActive ? "bg-yellow-50 text-yellow-700 hover:bg-yellow-100" : "bg-green-50 text-green-700 hover:bg-green-100"}`}>
            <Power className="w-4 h-4" /> {automation.isActive ? "Pause" : "Activate"}
          </button>
          <button onClick={onEdit} className="btn-primary text-sm"><Edit3 className="w-4 h-4" /> Edit</button>
          <button onClick={onDuplicate} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg" title="Duplicate"><Copy className="w-4 h-4" /></button>
          <button onClick={onDelete} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Total Runs", value: automation.runCount, icon: Activity, color: "text-blue-600 bg-blue-50" },
          { label: "Successes", value: automation.successCount, icon: CheckCircle2, color: "text-green-600 bg-green-50" },
          { label: "Failures", value: automation.failureCount, icon: XCircle, color: "text-red-600 bg-red-50" },
          { label: "Success Rate", value: `${successRate}%`, icon: TrendingUp, color: "text-emerald-600 bg-emerald-50" },
          { label: "Steps", value: automation.actions?.length || 0, icon: Zap, color: "text-purple-600 bg-purple-50" },
        ].map((s) => (
          <div key={s.label} className="card p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.color}`}><s.icon className="w-4 h-4" /></div>
            <div>
              <p className="text-xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Info */}
        <div className="space-y-6">
          {/* Trigger */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Trigger</h2>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${trigger?.color || "gray"}-100`}>
                <TriggerIcon className={`w-5 h-5 text-${trigger?.color || "gray"}-600`} />
              </div>
              <div>
                <p className="text-sm font-medium">{trigger?.label || automation.trigger}</p>
                <p className="text-xs text-gray-400">{trigger?.description}</p>
              </div>
            </div>
            {automation.triggerConfig && Object.keys(automation.triggerConfig).length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
                {Object.entries(automation.triggerConfig).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-xs">
                    <span className="text-gray-400">{k}</span>
                    <span className="text-gray-700 font-medium">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Timeline</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-400">Created</span><span className="text-gray-700">{new Date(automation.createdAt).toLocaleDateString()}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Updated</span><span className="text-gray-700">{new Date(automation.updatedAt).toLocaleDateString()}</span></div>
              {automation.lastRunAt && <div className="flex justify-between"><span className="text-gray-400">Last Run</span><span className="text-gray-700">{timeAgo(automation.lastRunAt)}</span></div>}
            </div>
          </div>
        </div>

        {/* Right: Workflow + Runs */}
        <div className="lg:col-span-2 space-y-6">
          {/* Workflow Steps */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Workflow ({automation.actions?.length || 0} steps)</h2>
            <div className="space-y-0">
              {(automation.actions || []).map((action, idx) => {
                const meta = ACTION_TYPES.find((a) => a.id === action.type);
                const ActionIcon = meta?.icon || Zap;
                return (
                  <div key={action.id || idx}>
                    {idx > 0 && (
                      <div className="flex items-center gap-2 py-1 pl-[22px]">
                        <div className="w-0.5 h-5 bg-gray-200" />
                        <ArrowRight className="w-3 h-3 text-gray-300 -ml-1.5" />
                      </div>
                    )}
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</div>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${meta?.color || "gray"}-100 flex-shrink-0`}>
                        <ActionIcon className={`w-4 h-4 text-${meta?.color || "gray"}-600`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{action.label}</p>
                        <p className="text-xs text-gray-400">{meta?.label}</p>
                      </div>
                      {action.config && Object.keys(action.config).length > 0 && (
                        <div className="text-xs text-gray-400 text-right max-w-[200px] truncate">
                          {Object.entries(action.config).slice(0, 2).map(([k, v]) => (
                            <span key={k} className="block truncate">{k}: {Array.isArray(v) ? v.join(", ") : String(v)}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Run History */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Run History</h2>
              <button onClick={() => setShowRuns(!showRuns)} className="text-xs text-indigo-600 font-medium hover:text-indigo-700">
                {showRuns ? "Collapse" : `Show All (${automation.runs?.length || 0})`}
              </button>
            </div>

            {!automation.runs || automation.runs.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No runs yet</p>
                <p className="text-xs text-gray-300">Use "Test Run" to execute this automation</p>
              </div>
            ) : (
              <div className="space-y-2">
                {(showRuns ? automation.runs : automation.runs.slice(0, 5)).map((run) => (
                  <div key={run.id}>
                    <button
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${expandedRun === run.id ? "border-indigo-200 bg-indigo-50/30" : "border-gray-100 hover:border-gray-200 bg-gray-50"}`}
                      onClick={() => setExpandedRun(expandedRun === run.id ? null : run.id)}
                    >
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${run.status === "success" ? "bg-green-500" : run.status === "failure" ? "bg-red-500" : "bg-gray-400"}`} />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-900 capitalize">{run.status}</span>
                        <span className="text-xs text-gray-400 ml-2">
                          {run.stepsRun}/{run.stepsTotal} steps • {run.duration}ms
                          {run.triggeredBy && ` • ${run.triggeredBy}`}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">{timeAgo(run.createdAt)}</span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedRun === run.id ? "rotate-180" : ""}`} />
                    </button>

                    {expandedRun === run.id && run.output && (
                      <div className="ml-6 mt-1 mb-2 space-y-1 border-l-2 border-indigo-200 pl-3">
                        {run.output.map((step: any, i: number) => (
                          <div key={i} className="flex items-start gap-2 text-xs py-1">
                            {step.status === "success" ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />}
                            <div>
                              <span className="font-medium text-gray-700">Step {step.step}: {step.label}</span>
                              <span className="text-gray-400 ml-1">({step.duration}ms)</span>
                              <p className="text-gray-500 mt-0.5">{step.result || step.error}</p>
                            </div>
                          </div>
                        ))}
                        {run.error && (
                          <div className="flex items-start gap-2 text-xs py-1 text-red-600">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            <span>{run.error}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
